import {AddFundsModal} from '#/components/add-funds/add-funds-modal';
import {CompleteUserProfileModal} from '#/components/add-funds/complete-user-profile-modal';
import {CountryNotSupportedModal} from '#/components/add-funds/country-not-supported-modal';
import {LoginModal} from '#/components/login/login-modal';
import {MobileNavigationModal} from '#/components/mobile-navigation/mobile-nav-modal';
import {SurveyModal} from '#/components/survey/survey-modal';
import {AddNativeTokensModal} from '#/components/testnet-account-funding/components/add-native-tokens-modal';
import {NewAccountFundedModal} from '#/components/testnet-account-funding/components/new-account-funded-modal';
import {UserFeedbackModal} from '#/components/user-feedback/user-feedback-modal';
import {UserProfileEditModal} from '#/components/user/user-profile-edit-modal';
import {WalletImportModal} from '#/components/wallet-import/wallet-import-modal';
import {WalletTransferModal} from '#/components/wallet-transfer/wallet-transfer-modal';
import {AfterLoginModal} from '#/components/welcome-modal/AfterLoginModal';
import {WelcomeModal} from '#/components/welcome-modal/welcome-modal';
import {UseObjectQueueSet, useObjectQueueSet} from '#/hooks/use-queue-set';
import {DefaultChildren} from '#/utils/utils';
import {AnimatePresence} from 'framer-motion';
import {ComponentProps, FunctionComponent, createContext, useContext} from 'react';

type ModalNameAndProps =
  | {
      name: 'NewAccountFundedModal';
      props: ComponentProps<typeof NewAccountFundedModal>;
    }
  | {
      name: 'AddNativeTokensModal';
      props: ComponentProps<typeof AddNativeTokensModal>;
    }
  | {
      name: 'UserFeedbackModal';
      props: ComponentProps<typeof UserFeedbackModal>;
    }
  | {
      name: 'UserProfileEditModal';
      props: ComponentProps<typeof UserProfileEditModal>;
    }
  | {
      name: 'WalletTransferModal';
      props: ComponentProps<typeof WalletTransferModal>;
    }
  | {
      name: 'WalletImportModal';
      props: ComponentProps<typeof WalletImportModal>;
    }
  | {
      name: 'CompleteUserProfileModal';
      props: ComponentProps<typeof CompleteUserProfileModal>;
    }
  | {
      name: 'AddFundsModal';
      props: ComponentProps<typeof AddFundsModal>;
    }
  | {
      name: 'CountryNotSupportedModal';
      props: ComponentProps<typeof CountryNotSupportedModal>;
    }
  | {
      name: 'WelcomeModal';
      props: ComponentProps<typeof WelcomeModal>;
    }
  | {
      name: 'SurveyModal';
      props: ComponentProps<typeof SurveyModal>;
    }
  | {
      name: 'AfterLoginModal';
      props: ComponentProps<typeof AfterLoginModal>;
    }
  | {
      name: 'LoginModal';
      props: ComponentProps<typeof LoginModal>;
    }
  | {
      name: 'MobileNavigationModal';
      props: ComponentProps<typeof MobileNavigationModal>;
    };

export type ModalContextValue = {modals: ModalNameAndProps[]} & Omit<UseObjectQueueSet<ModalNameAndProps>, 'values'>;

const initialValue: ModalContextValue = {} as ModalContextValue;
export const ModalContext = createContext<ModalContextValue>(initialValue);

export const ModalProvider: FunctionComponent<DefaultChildren> = ({children}) => {
  const {values: modals, ...rest} = useObjectQueueSet<ModalNameAndProps>({
    initial: [],
    findCallbackComparable: (a, b) => a.name === b.name,
  });

  return (
    <ModalContext.Provider value={{modals, ...rest}}>
      <Modals />
      {children}
    </ModalContext.Provider>
  );
};

export const useModalContext = () => {
  const modalContext = useContext(ModalContext);
  return modalContext;
};

const Modals: FunctionComponent = () => {
  // Context
  const {modals} = useContext(ModalContext);

  return (
    <AnimatePresence>
      {modals.map((modalNameAndProps: ModalNameAndProps, index: number) => (
        <AppModal key={`app-modal-${index}`} modalNameAndProps={modalNameAndProps} />
      ))}
    </AnimatePresence>
  );
};

const AppModal: FunctionComponent<{modalNameAndProps: ModalNameAndProps}> = ({modalNameAndProps}) => {
  switch (modalNameAndProps.name) {
    case 'NewAccountFundedModal':
      return <NewAccountFundedModal {...modalNameAndProps.props} />;
    case 'AddNativeTokensModal':
      return <AddNativeTokensModal {...modalNameAndProps.props} />;
    case 'UserFeedbackModal':
      return <UserFeedbackModal {...modalNameAndProps.props} />;
    case 'UserProfileEditModal':
      return <UserProfileEditModal {...modalNameAndProps.props} />;
    case 'WalletTransferModal':
      return <WalletTransferModal {...modalNameAndProps.props} />;
    case 'WalletImportModal':
      return <WalletImportModal {...modalNameAndProps.props} />;
    case 'CompleteUserProfileModal':
      return <CompleteUserProfileModal {...modalNameAndProps.props} />;
    case 'AddFundsModal':
      return <AddFundsModal {...modalNameAndProps.props} />;
    case 'CountryNotSupportedModal':
      return <CountryNotSupportedModal {...modalNameAndProps.props} />;
    case 'WelcomeModal':
      return <WelcomeModal {...modalNameAndProps.props} />;
    case 'SurveyModal':
      return <SurveyModal {...modalNameAndProps.props} />;
    case 'AfterLoginModal':
      return <AfterLoginModal {...modalNameAndProps.props} />;
    case 'LoginModal':
      return <LoginModal {...modalNameAndProps.props} />;
    case 'MobileNavigationModal':
      return <MobileNavigationModal {...modalNameAndProps.props} />;
    default:
      return null;
  }
};
